// Lifted verbatim from saas `apps/frontend/src/features/security/cyberEssentialsCopy.ts` at
// `eb5d377d` (#639, D14); the old guard, `test_cyber_essentials_copy.py`, is ported as
// `CyberEssentials.test.ts` beside this file.

/**
 * Cyber Essentials copy, in one place so a guard can pin it by identity (#1396).
 *
 * This is the highest-risk copy in the product. Cyber Essentials is a
 * certification, and a buyer who reads certification progress into this page
 * would be relying on it for a compliance decision we cannot support: the scheme
 * is assessed against a whole organisation including end-user devices, and this
 * product sees an AWS control plane.
 *
 * The copy lives here rather than inline in JSX so `CyberEssentials.test.ts`
 * can read it as text and assert BOTH halves: that the disclaimer is present, and
 * that a forbidden set of progress-implying phrases is absent. The second half is
 * the load-bearing one — a shape-only guard passes with a falsehood reinstated
 * beside the disclaimer, which is exactly how #1305 shipped.
 *
 * ⚠️ Per the standing rule in this codebase: do NOT add copy routing the reader
 * to a support channel, an advisor, or an assessor referral. No such channel
 * exists, and inventing a destination is its own honest-surfaces defect.
 */

/** The claim the page must always make. Pinned verbatim by the backend guard. */
export const CE_DISCLAIMER =
  "This supports preparation only. It does not constitute or contribute to Cyber Essentials certification, and no result here is evidence for an assessment.";

/** Why the page exists at all, given it cannot measure the scheme. */
export const CE_INTRO =
  "Cyber Essentials is five controls, assessed across your whole organisation — including laptops, phones and the people using them. An AWS security scan can evidence only part of each, and most of the scheme not at all.";

/** Heading for the half of each control we cannot see. Not a footnote. */
export const CE_OUT_OF_SCOPE_HEADING = "What this does not cover";

/** Heading for the half we can. */
export const CE_EVIDENCE_HEADING = "What we can evidence";

/**
 * Why a control can read NOT_OBSERVABLE rather than PASS or FAIL (#1299).
 *
 * Every signal behind such a control is a detector-enabled check — "is GuardDuty
 * switched on" — which reports whether we can SEE, not what the state is. A
 * customer running endpoint anti-malware everywhere would still fail those
 * checks, so calling it a control failure would be false.
 */
export const CE_NOT_OBSERVABLE_NOTE =
  "We cannot see this. Every signal we have for this control reports whether an AWS detection service is switched on, which is a different question from whether the control is in place.";

/** Shown when no scan has run. The scope limit still applies. */
export const CE_NO_SCAN_NOTE =
  "No completed scan yet, so nothing below has been assessed. What this page cannot cover does not depend on a scan, and is shown regardless.";

/** Human labels for the per-control verdict. */
export const CE_STATUS_LABEL: Record<string, string> = {
  PASS: "No failures in what we checked",
  FAIL: "Failures found in what we checked",
  NOT_OBSERVABLE: "Not observable",
  NOT_ASSESSED: "Not assessed",
};

/** Human labels for how much of a control we reach. Never says "full". */
export const CE_COVERAGE_LABEL: Record<string, string> = {
  partial: "Partial — cloud only",
  weak: "Weak — little we can evidence",
  none: "None — nothing we can evidence",
};
